import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { endpointPrincipal } from "../api/endPoints/endpointPrincipal/endpointPrincipal";
import { ColunaTabela } from "../components/Formularios/Table/CustomTable";

export type ColunaPreferencia<T = any> = ColunaTabela<T> & { hidden: boolean };

interface PreferenciasGridResponse {
  resultado?: {
    colunasVisiveis?: string[];
    ordemColunas?: string[];
    itensPorPagina?: number;
  } | null;
}

interface SalvarPreferenciasPayload<T> {
  colunas: ColunaPreferencia<T>[];
  itensPorPagina: number;
}

// Preferências de colunas/itens por página por usuário e tela (mesmo padrão do XC WMS)
const usePreferenciasColunas = <T = any>(
  tela: string,
  colunasPadrao: ColunaTabela<T>[],
  itensPorPaginaPadrao: number = 10,
) => {
  // ordem: chaves de todas as colunas (visíveis e ocultas) na ordem escolhida
  const [ordem, setOrdem] = useState<string[]>([]);
  const [ocultas, setOcultas] = useState<string[]>([]);
  const [itensPorPagina, setItensPorPaginaPreferencia] =
    useState<number>(itensPorPaginaPadrao);
  const [modalAberto, setModalAberto] = useState(false);
  const [loading, setLoading] = useState(false);
  const [carregado, setCarregado] = useState(false);

  useEffect(() => {
    if (!tela) return;

    const carregarPreferencias = async () => {
      setLoading(true);

      try {
        const { data } = await endpointPrincipal.get<PreferenciasGridResponse>(
          `/preferenciasgrid/${tela}`,
        );

        const colunasSalvas = data?.resultado?.colunasVisiveis || [];
        const ordemSalva = data?.resultado?.ordemColunas || [];
        const itensPorPaginaSalvo = data?.resultado?.itensPorPagina;

        if (colunasSalvas.length) {
          setOrdem(ordemSalva.length ? ordemSalva : colunasSalvas);
          setOcultas(ordemSalva.filter((key) => !colunasSalvas.includes(key)));
        } else {
          setOrdem([]);
          setOcultas([]);
        }

        if (
          typeof itensPorPaginaSalvo === "number" &&
          itensPorPaginaSalvo > 0
        ) {
          setItensPorPaginaPreferencia(itensPorPaginaSalvo);
        } else {
          setItensPorPaginaPreferencia(itensPorPaginaPadrao);
        }
      } catch (err: any) {
        setOrdem([]);
        setOcultas([]);
        setItensPorPaginaPreferencia(itensPorPaginaPadrao);
        console.warn("Erro ao carregar preferências:", err);
      } finally {
        setLoading(false);
        setCarregado(true);
      }
    };

    carregarPreferencias();
  }, [tela, itensPorPaginaPadrao]);

  // Todas as colunas na ordem salva. Colunas novas (fora da ordem salva) vão para o fim, visíveis.
  const colunasOrdenadas = useMemo<ColunaPreferencia<T>[]>(() => {
    const posicao = (key: string) => {
      const index = ordem.indexOf(key);
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    };

    return colunasPadrao
      .map((coluna, indexPadrao) => ({ coluna, indexPadrao }))
      .sort(
        (a, b) =>
          posicao(a.coluna.key) - posicao(b.coluna.key) ||
          a.indexPadrao - b.indexPadrao,
      )
      .map(({ coluna }) => ({ ...coluna, hidden: ocultas.includes(coluna.key) }));
  }, [colunasPadrao, ordem, ocultas]);

  const colunasVisiveis = useMemo<ColunaTabela<T>[]>(
    () =>
      colunasOrdenadas
        .filter((coluna) => !coluna.hidden)
        .map(({ hidden, ...coluna }) => coluna),
    [colunasOrdenadas],
  );

  const abrirModal = () => setModalAberto(true);
  const fecharModal = () => setModalAberto(false);

  const salvarPreferencias = async ({
    colunas,
    itensPorPagina,
  }: SalvarPreferenciasPayload<T>) => {
    setLoading(true);

    try {
      setOrdem(colunas.map((c) => c.key));
      setOcultas(colunas.filter((c) => c.hidden).map((c) => c.key));
      setItensPorPaginaPreferencia(itensPorPagina);

      await endpointPrincipal.post(`/preferenciasgrid/${tela}`, {
        colunasVisiveis: colunas.filter((c) => !c.hidden).map((c) => c.key),
        ordemColunas: colunas.map((c) => c.key),
        itensPorPagina,
      });

      toast.success("Preferências salvas com sucesso!");
    } catch (error: any) {
      toast.error(
        "Erro ao salvar preferências: " +
          (error.response?.data?.message || error.message),
      );
    } finally {
      fecharModal();
      setLoading(false);
    }
  };

  const resetarPreferencias = async () => {
    setLoading(true);

    try {
      setOrdem([]);
      setOcultas([]);
      setItensPorPaginaPreferencia(itensPorPaginaPadrao);

      await endpointPrincipal.post(`/preferenciasgrid/${tela}`, {
        colunasVisiveis: colunasPadrao.map((c) => c.key),
        ordemColunas: colunasPadrao.map((c) => c.key),
        itensPorPagina: itensPorPaginaPadrao,
      });

      toast.success("Preferências resetadas com sucesso!");
    } catch (error: any) {
      toast.error(
        "Erro ao salvar preferências: " +
          (error.response?.data?.message || error.message),
      );
    } finally {
      fecharModal();
      setLoading(false);
    }
  };

  return {
    colunasVisiveis,
    colunasOrdenadas,
    itensPorPagina,
    modalAberto,
    setModalAberto,
    abrirModal,
    fecharModal,
    salvarPreferencias,
    resetarPreferencias,
    loading,
    carregado,
  };
};

export default usePreferenciasColunas;
